var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
function createAudioChannels(channel, keys, code, volumeRanges, name, nameQuery) {
    const getCode = typeof code === 'number' ? (key, index) => (code + index).toString() : code;
    const getName = typeof name === 'string' ? (key, index) => `${name}${key[1]}` : name;
    return keys.map((key, index) => new channel(key, getCode(key, index), volumeRanges, getName === null || getName === void 0 ? void 0 : getName(key, index), nameQuery === null || nameQuery === void 0 ? void 0 : nameQuery(key, index)));
}
function getIndices(codes, config) {
    return codes.length > 1 && typeof config !== 'string' && config.stereo !== 'stereo'
        ? [config.stereo === 'left' ? 0 : 1]
        : codes.map((e, i) => i);
}
// range1d('X', 1, 4) --> ['X1', 'X2', 'X3', 'X4']
// range1d('X', 'A', 'D') --> ['XA', 'XB', 'XC', 'XD']
function range1d(prefix, start, end) {
    const values = [];
    let isChar = false;
    if (typeof start === 'string') {
        isChar = true;
        start = start.charCodeAt(0);
        if (typeof end !== 'string')
            throw new Error('incompatible range types');
        end = end.charCodeAt(0);
    }
    else {
        if (typeof end !== 'number')
            throw new Error('incompatible range types');
    }
    for (let i = start; i <= end; i++) {
        const index = isChar ? String.fromCharCode(i) : i.toString();
        values.push(`${prefix}${index}`);
    }
    return values;
}
// range2d('X', '1', 'A', '4', 'D') -> ['X1A', 'X1B', 'X1C', 'X1D', ..., 'X4A', 'X4B', 'X4C', 'X4D']
// range2d('Z', 1, 1, 2, 16) -> ['Z11', 'Z12', ..., 'Z116', 'Z21', 'Z22', 'Z216']
function range2d(prefix, rowStart, colStart, rowEnd, colEnd, separator = '') {
    const values = [];
    let rowIsChar = false;
    let colIsChar = false;
    if (typeof rowStart === 'string') {
        rowIsChar = true;
        rowStart = rowStart.charCodeAt(0);
        if (typeof rowEnd !== 'string')
            throw new Error('incompatible row types');
        rowEnd = rowEnd.charCodeAt(0);
    }
    else {
        if (typeof rowEnd !== 'number')
            throw new Error('incompatible row types');
    }
    if (typeof colStart === 'string') {
        colIsChar = true;
        colStart = colStart.charCodeAt(0);
        if (typeof colEnd !== 'string')
            throw new Error('incompatible column types');
        colEnd = colEnd.charCodeAt(0);
    }
    else {
        if (typeof colEnd !== 'number')
            throw new Error('incompatible column types');
    }
    for (let i = rowStart; i <= rowEnd; i++) {
        const row = rowIsChar ? String.fromCharCode(i) : i.toString();
        for (let j = colStart; j <= colEnd; j++) {
            const col = colIsChar ? String.fromCharCode(j) : j.toString();
            values.push(`${prefix}${row}${separator}${col}`);
        }
    }
    return values;
}
class ExtronAudioChannelBase {
    constructor(key, volumeRanges, defaultName, nameQuery) {
        this.key = key;
        this.volumeRanges = volumeRanges;
        this.nameQuery = nameQuery;
        this._name = '';
        this.isMuteConfigured = false;
        this.isVolumeConfigured = false;
        this.gainConfig = undefined;
        this.hasMute = true;
        this.hasStereoConfig = false;
        this.hasVolume = this.volumeRanges !== undefined;
        if (defaultName)
            this._name = defaultName;
    }
    get name() {
        return this._name;
    }
    getVolumeRange(config, type) {
        var _a;
        const range = (_a = this.volumeRanges) === null || _a === void 0 ? void 0 : _a[type];
        if (range !== undefined) {
            return Object.assign(Object.assign({}, range), { round: 1 });
        }
        return undefined;
    }
    processMute(config, data) {
        return undefined;
    }
    processVolume(config, data) {
        return undefined;
    }
    queryCaps(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () { });
    }
    queryName(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.nameQuery === undefined)
                return;
            const match = yield socket.send(this.nameQuery, /.+,(.+)/, tags);
            this._name = match[1];
        });
    }
    queryMute(config, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return false;
        });
    }
    queryVolume(config, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return 0;
        });
    }
    setMute(config, value, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return false;
        });
    }
    setVolume(config, value, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return 0;
        });
    }
}
class CommonMute extends ExtronAudioChannelBase {
    constructor(key, codes, volumeRanges, defaultName, nameQuery) {
        super(key, volumeRanges, defaultName, nameQuery);
        this.muteRegExps = [];
        this.codes = typeof codes === 'string' ? [codes] : codes;
        this.hasStereoConfig = this.codes.length > 1;
        this.muteRegExps = this.codes.map(code => new RegExp(`DsM${code}\\*(0|1)`));
    }
    processMute(config, data) {
        const index = getIndices(this.codes, config)[0];
        const match = this.muteRegExps[index].exec(data);
        if (match)
            return match[1] === '1';
        return undefined;
    }
    queryMute(config, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const index = this.codes.length > 1 && typeof config !== 'string' && config.stereo === 'right'
                ? 1
                : 0;
            const match = yield socket.send(`\x1bM${this.codes[index]}AU`, this.muteRegExps[index], tags);
            return match[1] === '1';
        });
    }
    setMute(config, value, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const indices = getIndices(this.codes, config);
            let newValue = false;
            for (const i of indices) {
                const code = this.codes[i];
                const regExp = this.muteRegExps[i];
                const match = yield socket.send(`\x1bM${code}*${value ? '1' : '0'}AU`, regExp, tags);
                if (i === 0)
                    newValue = match[1] === '1';
            }
            return newValue;
        });
    }
}
class Crosspoint extends CommonMute {
    constructor(key, codes, volumeRanges, defaultName, nameQuery) {
        super(key, codes, volumeRanges, defaultName, nameQuery);
        this.hasStereoConfig = false;
        this.volumeRegExp = new RegExp(`(?:In${key[key.length - 1]}\s*Aud)(\-?\d+)`);
    }
    processVolume(config, data) {
        const match = this.volumeRegExp.exec(data);
        if (match)
            return parseInt(match[1], 10);
        return undefined;
    }
    queryVolume(config, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield socket.send(`${this.key[this.key.length - 1]}G`, this.volumeRegExp, tags);
            return parseInt(match[1], 10);
        });
    }
    setVolume(config, value, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield socket.send(`${this.key[this.key.length - 1]}*${value}G`, this.volumeRegExp, tags);
            return parseInt(match[1], 10);
        });
    }
}
class Group extends CommonMute {
    constructor(key, codes, hasZeroPad, volumeRanges, groupVolumeRanges, nameQuery) {
        super(key, codes, volumeRanges, nameQuery);
        this.hasZeroPad = hasZeroPad;
        this.groupVolumeRanges = groupVolumeRanges;
        if (volumeRanges === undefined && this.groupVolumeRanges !== undefined) {
            this.gainConfig = Object.keys(this.groupVolumeRanges);
        }
        this._name = `Group ${this.codes[0]}`;
        this.muteRegExps = this.codes.map(code => {
            const paddedCode = this.hasZeroPad ? code.padStart(2, '0') : code;
            return new RegExp(`GrpmD${paddedCode}\\*\\+?[0]{0,4}(0|1)`, 'i');
        });
        this.volumeRegExps = this.codes.map(code => {
            const paddedCode = this.hasZeroPad ? code.padStart(2, '0') : code;
            return new RegExp(`GrpmD${paddedCode}\\*([\\-\\+]?\\d+)`, 'i');
        });
    }
    static createAudioChannels(keys, hasZeroPad, volumeRanges, groupVolumeRanges) {
        const aCode = 'A'.charCodeAt(0);
        const zCode = 'Z'.charCodeAt(0);
        return keys.map(key => {
            let index = 1;
            while (key.charCodeAt(index) >= aCode && key.charCodeAt(index) <= zCode) {
                index++;
            }
            return new Group(key, `${key.substring(index)}`, hasZeroPad, volumeRanges, groupVolumeRanges);
        });
    }
    getVolumeRange(config, type) {
        var _a, _b;
        let range;
        if (this.volumeRanges !== undefined) {
            range = this.volumeRanges[type];
        }
        else if (this.groupVolumeRanges !== undefined) {
            if (typeof config === 'string') {
                range = (_a = this.groupVolumeRanges.outputAttenuation) === null || _a === void 0 ? void 0 : _a[type];
            }
            else {
                if (config.gain !== undefined)
                    range = (_b = this.groupVolumeRanges[config.gain]) === null || _b === void 0 ? void 0 : _b[type];
            }
        }
        return range === undefined ? undefined : Object.assign(Object.assign({}, range), { round: 1 });
    }
    processVolume(config, data) {
        const index = getIndices(this.codes, config)[0];
        const match = this.volumeRegExps[index].exec(data);
        if (match)
            return parseInt(match[1], 10);
        return undefined;
    }
    queryCaps(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const code = this.hasZeroPad ? this.codes[0].padStart(2, '0') : this.codes[0];
            const match = yield socket.send(`\x1bP${code}GRPM`, new RegExp(`GrpmP${code}\\*(\\d+)`, 'i'), tags);
            const groupType = parseInt(match[1], 10);
            this.hasMute = groupType === 12;
            this.hasVolume = groupType === 6;
            if (!this.hasVolume)
                this.gainConfig = undefined;
        });
    }
    queryMute(config, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const index = this.codes.length > 1 && typeof config !== 'string' && config.stereo === 'right'
                ? 1
                : 0;
            const code = this.hasZeroPad ? this.codes[index].padStart(2, '0') : this.codes[index];
            const regExp = this.muteRegExps[index];
            // Prepend \x0a because group commands don't like to follow other commands.
            const match = yield socket.send(`\x0a\x1bD${code}GRPM`, regExp, tags);
            return match[1] === '1';
        });
    }
    queryVolume(config, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const index = this.codes.length > 1 && typeof config !== 'string' && config.stereo === 'right'
                ? 1
                : 0;
            const code = this.hasZeroPad ? this.codes[index].padStart(2, '0') : this.codes[index];
            const regExp = this.volumeRegExps[index];
            // Prepend \x0a because group commands don't like to follow other commands.
            const match = yield socket.send(`\x0a\x1bD${code}GRPM`, regExp, tags);
            return parseInt(match[1], 10);
        });
    }
    setMute(config, value, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const indices = getIndices(this.codes, config);
            let newValue = false;
            for (const i of indices) {
                const code = this.hasZeroPad ? this.codes[i].padStart(2, '0') : this.codes[i];
                const regExp = this.muteRegExps[i];
                const mute = value ? '1' : '0';
                // Prepend \x0a because group commands don't like to follow other commands.
                const match = yield socket.send(`\x0a\x1bD${code}*${mute}GRPM`, regExp, tags);
                if (i === 0)
                    newValue = match[1] === '1';
            }
            return newValue;
        });
    }
    setVolume(config, value, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const indices = getIndices(this.codes, config);
            let newValue = 0;
            for (const i of indices) {
                const code = this.hasZeroPad ? this.codes[i].padStart(2, '0') : this.codes[i];
                const regExp = this.volumeRegExps[i];
                // Prepend \x0a because group commands don't like to follow other commands.
                const match = yield socket.send(`\x0a\x1bD${code}*${value}GRPM`, regExp, tags);
                if (i === 0)
                    newValue = parseInt(match[1], 10);
            }
            return newValue;
        });
    }
}
class MatrixChannel extends CommonMute {
    constructor(key, codes, volumeRanges, defaultName, nameQuery) {
        super(key, codes, volumeRanges, defaultName, nameQuery);
        this.volumeRegExps = this.codes.map(code => new RegExp(`DsG${code}\\*(\\-?\\d+)`));
    }
    processVolume(config, data) {
        const index = getIndices(this.codes, config)[0];
        const match = this.volumeRegExps[index].exec(data);
        if (match)
            return parseInt(match[1], 10);
        return undefined;
    }
    queryVolume(config, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const index = this.codes.length > 1 && typeof config !== 'string' && config.stereo === 'right'
                ? 1
                : 0;
            const code = this.codes[index];
            const regExp = this.volumeRegExps[index];
            const match = yield socket.send(`\x1bG${code}AU`, regExp, tags);
            return parseInt(match[1], 10);
        });
    }
    setVolume(config, value, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const indices = getIndices(this.codes, config);
            let newValue = 0;
            for (const i of indices) {
                const code = this.codes[i];
                const regExp = this.volumeRegExps[i];
                const match = yield socket.send(`\x1bG${code}*${value}AU`, regExp, tags);
                if (i === 0)
                    newValue = parseInt(match[1], 10);
            }
            return newValue;
        });
    }
}
class VariableOutput extends ExtronAudioChannelBase {
    constructor(key, volumeRanges, defaultName, nameQuery) {
        super(key, volumeRanges, defaultName, nameQuery);
        this.muteRegExp = /Amt(0|1)/;
        this.volumeRegExp = /Vol(\-?\d+)/;
        this.hasStereoConfig = false;
    }
    processMute(config, data) {
        const match = this.muteRegExp.exec(data);
        if (match)
            return match[1] === '1';
        return undefined;
    }
    processVolume(config, data) {
        const match = this.volumeRegExp.exec(data);
        if (match)
            return parseInt(match[1], 10);
        return undefined;
    }
    queryMute(config, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield socket.send('Z', this.muteRegExp, tags);
            return match[1] === '1';
        });
    }
    queryVolume(config, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield socket.send(`V`, this.volumeRegExp, tags);
            return parseInt(match[1], 10);
        });
    }
    setMute(config, value, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield socket.send(`${value ? '1' : '0'}Z`, this.muteRegExp, tags);
            return match[1] === '1';
        });
    }
    setVolume(config, value, socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield socket.send(`${value}V`, this.volumeRegExp, tags);
            return parseInt(match[1], 10);
        });
    }
}
class ExtronDmp44 {
    constructor(equipmentType, modelNumber, extronModel) {
        this.equipmentType = equipmentType;
        this.modelNumber = modelNumber;
        this.hasVerboseMode = true;
    }
    static deviceMatches(equipmentType, modelNumber, extronModel) {
        return (equipmentType === '-LvpXhNbQRIOWO_3gtUS' &&
            (modelNumber === '-Mj-EI0DyjevoVaHx3ek' ||
                ((modelNumber === '-Mj-Ei1OBYjLMZLZE-D9' || modelNumber === undefined) &&
                    extronModel === 'dmp44lc')));
    }
    getAudioChannels() {
        return [
            ...createAudioChannels(MatrixChannel, range1d('I', 1, 4), 30100, {
                volume: { min: -440, max: 60 },
                trim: { min: -180, max: 60 }, // -18 dB to 6 dB
            }, 'Input ', (key, index) => `\x1b${index + 1}NI`),
            ...createAudioChannels(MatrixChannel, range2d('X', 1, 1, 4, 4), (key, index) => (20000 + 100 * (index >> 2) + (index % 4)).toString(), {
                volume: { min: -240, max: 120 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, (key, index) => `Crosspoint ${key[1]} to ${key[2]}`),
            ...createAudioChannels(MatrixChannel, range1d('O', 1, 4), 60000, {
                volume: { min: -500, max: 0 },
                trim: { min: -240, max: 0 }, // -24 dB to 0 dB
            }, 'Output ', (key, index) => `\x1b${index + 1}NO`),
        ];
    }
}
class ExtronDmp64 {
    constructor(equipmentType, modelNumber, extronModel) {
        this.equipmentType = equipmentType;
        this.modelNumber = modelNumber;
        this.hasVerboseMode = true;
    }
    static deviceMatches(equipmentType, modelNumber, extronModel) {
        return (equipmentType === '-LvpXhNbQRIOWO_3gtUS' &&
            (modelNumber === '-Mj-EPSclr1awY8Mex-I' ||
                ((modelNumber === '-Mj-Ei1OBYjLMZLZE-D9' || modelNumber === undefined) &&
                    extronModel === 'dmp64')));
    }
    getAudioChannels() {
        return [
            ...createAudioChannels(MatrixChannel, [...range1d('I', 1, 6), ...range1d('I', 'A', 'D')], (key, index) => {
                let n = parseInt(key[1], 10);
                if (!Number.isNaN(n))
                    return (40100 + n - 1).toString();
                n = key.charCodeAt(1) - 'A'.charCodeAt(0);
                return (50000 + n).toString();
            }, {
                volume: { min: 1668, max: 2168 },
                trim: { min: 1928, max: 2168 }, // -12 dB to 12 dB
            }, 'Input ', (key, index) => {
                let n = parseInt(key[1], 10);
                if (!Number.isNaN(n))
                    return `\x1b0${n}NI`;
                return undefined;
            }),
            ...createAudioChannels(MatrixChannel, [...range2d('X', 1, 1, 6, 4), ...range2d('X', 'A', 1, 'D', 4)], (key, index) => (20000 + 100 * (index >> 2) + (index % 4)).toString(), {
                volume: { min: 1799, max: 2298 },
                trim: { min: 1928, max: 2168 }, // -12 dB to 12 dB (X61 in Extron manual)
            }, (key, index) => `Crosspoint ${key[1]} to ${key[2]}`),
            ...createAudioChannels(MatrixChannel, range1d('O', 1, 4), 60000, {
                volume: { min: 1548, max: 2048 },
                trim: { min: 1808, max: 2048 }, // -24 dB to 0 dB
            }, 'Output ', (key, index) => `\x1b0${index + 1}NO`),
            ...Group.createAudioChannels(range1d('G', 1, 32), true, undefined, {
                mixPoint: {
                    volume: { min: -250, max: 250 },
                    trim: { min: -120, max: 120 },
                },
                preMatrixTrim: {
                    volume: { min: -120, max: 120 },
                    trim: { min: -120, max: 120 },
                },
                preMixerGain: {
                    volume: { min: -380, max: 120 },
                    trim: { min: -120, max: 120 },
                },
                outputAttenuation: {
                    volume: { min: -500, max: 0 },
                    trim: { min: -240, max: 0 },
                },
            }),
        ];
    }
}
class ExtronDtpSeries {
    constructor(equipmentType, modelNumber, extronModel) {
        this.equipmentType = equipmentType;
        this.modelNumber = modelNumber;
        this.hasVerboseMode = true;
        this.queryVideoCommand = '!';
        this.useOutputInRouteCommands = true;
    }
    static deviceMatches(equipmentType, modelNumber, extronModel) {
        return equipmentType === '-LCEDOmeRpYN8cVsR6cq' && modelNumber === '-Mj-Btjj3g11wa-VRTfm';
    }
    getAudioChannels() {
        const numInputs = 8;
        const numOutputs = 4;
        return [
            // TODO: Craig's comments say mute isn't supported for these channels.
            // line inputs (block A in Figure 41); note that these are the trim blocks, which are
            // appropriate for volume control; we do not include the gain blocks, which should be
            // used for equipment calibration
            ...createAudioChannels(MatrixChannel, range1d('A', 1, numInputs), (key, index) => [
                (30100 + 2 * index).toString(),
                (30100 + 2 * index + 1).toString(),
            ], { trim: { min: -120, max: 120 } }, // -12 dB to 12 dB
            'Input '),
            // microphone inputs (block B in Figure 41)
            ...createAudioChannels(MatrixChannel, range1d('B', 1, 4), 40100, {
                volume: { min: -380, max: 120 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, 'Mic '),
            // post matrix gain blocks (block F in Figure 41)
            ...createAudioChannels(MatrixChannel, range1d('F', 1, numOutputs), (key, index) => [
                (50000 + 2 * index).toString(),
                (50000 + 2 * index + 1).toString(),
            ], {
                volume: { min: -380, max: 120 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, 'Post Matrix Output '),
            // virtual returns (block D in Figure 41)
            ...createAudioChannels(MatrixChannel, range1d('D', 'A', 'H'), 50100, {
                volume: { min: -380, max: 120 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, 'Virtual Return '),
            // expansion inputs (block E in Figure 41)
            ...createAudioChannels(MatrixChannel, range1d('E', 1, 8), 50200, {
                volume: { min: -250, max: 250 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, (key, index) => `Expansion Input ${index + 1}`),
            // matrix outputs to outputs (block G in figure 41)
            ...createAudioChannels(MatrixChannel, Array(numOutputs)
                .fill(0)
                .map((el, index) => `G${index + 1}${index + 1}`), (key, index) => [
                (20000 + 2 * index * 101).toString(),
                (20000 + (2 * index + 1) * 101).toString(),
            ], {
                volume: { min: -250, max: 250 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, (key, index) => `Matrix Output ${key[1]} to Output ${key[2]}`),
            // mic in to audio outputs (block H in Figure 41)
            ...createAudioChannels(MatrixChannel, range2d('H', 1, 1, 4, numOutputs), (key, index) => {
                const base = 20800 + 100 * Math.trunc(index / numOutputs) + 2 * (index % numOutputs);
                return [base.toString(), (base + 1).toString()];
            }, {
                volume: { min: -250, max: 250 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, (key, index) => `Mic ${key[1]} to Output ${key.substring(2)}`),
            // virtual ret to audio outputs (block I in figure 41)
            ...createAudioChannels(MatrixChannel, range2d('I', 'A', 1, 'H', numOutputs), (key, index) => {
                const base = 21200 + 100 * Math.trunc(index / numOutputs) + 2 * (index % numOutputs);
                return [base.toString(), (base + 1).toString()];
            }, {
                volume: { min: -250, max: 250 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, (key, index) => `Virtual Return ${key[1]} to Output ${key.substring(2)}`),
            // expansion to audio outputs (block J in figure 41)
            ...createAudioChannels(MatrixChannel, range2d('J', 1, 1, 8, numOutputs), (key, index) => {
                const base = 22000 + 100 * Math.trunc(index / numOutputs) + 2 * (index % numOutputs);
                return [base.toString(), (base + 1).toString()];
            }, {
                volume: { min: -250, max: 250 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, (key, index) => `Expansion Input ${key[1]} to Output ${key.substring(2)}`),
            // analog line outputs, HDMI/DTP outputs, and amplifier output (block K in figure 41)
            ...createAudioChannels(MatrixChannel, [
                ...range1d('KA', 1, numOutputs),
                ...range1d('KV', 1, numOutputs),
                'KP1',
                ...range1d('KD', (numOutputs >> 1) + 1, numOutputs),
            ], (key, index) => {
                var _a;
                const n = 2 * (parseInt(key[key.length - 1], 10) - 1);
                const base = ((_a = { A: 60000, V: 60200 }[key[1]]) !== null && _a !== void 0 ? _a : 60300) + n;
                return [base.toString(), (base + 1).toString()];
            }, {
                volume: { min: -500, max: 0 },
                trim: { min: -240, max: 0 },
            }, (key, index) => {
                var _a;
                const type = (_a = { A: 'Analog', V: 'Digital', D: 'Analog DTP' }[key[1]]) !== null && _a !== void 0 ? _a : 'Amp';
                return `Output ${key[key.length - 1]} ${type} Volume`;
            }),
            // groups (use "Z" so we don't conflict with figure labels above)
            ...Group.createAudioChannels(range1d('Z', 1, 32), false, undefined, {
                mixPoint: {
                    volume: { min: -250, max: 250 },
                    trim: { min: -120, max: 120 },
                },
                preMatrixTrim: {
                    volume: { min: -120, max: 120 },
                    trim: { min: -120, max: 120 },
                },
                preMixerGain: {
                    volume: { min: -380, max: 120 },
                    trim: { min: -120, max: 120 },
                },
                outputAttenuation: {
                    volume: { min: -500, max: 0 },
                    trim: { min: -240, max: 0 },
                },
            }),
        ];
    }
}
class ExtronDtp4kSeries {
    constructor(equipmentType, modelNumber, extronModel) {
        this.equipmentType = equipmentType;
        this.modelNumber = modelNumber;
        this.hasVerboseMode = true;
        this.queryVideoCommand = '!';
        this.useOutputInRouteCommands = true;
    }
    static deviceMatches(equipmentType, modelNumber, extronModel) {
        return (equipmentType === '-LCEDOmeRpYN8cVsR6cq' &&
            (modelNumber === '-LQxjUuabdG7N0VUheIE' || // DTP844k
                modelNumber === '-LQxjTsl7pkexzC7R7Sh' || // DTP824k
                modelNumber === '-LQxjW6cqOwgv7nHi4RB' || // DTP864k
                modelNumber === '-LQxjX4z4fD22aBP5sge') // DTP1084k
        );
    }
    getAudioChannels() {
        let numInputs = 8;
        let numOutputs = 4;
        if (this.modelNumber === '-LQxjTsl7pkexzC7R7Sh') {
            // DTP824k
            numOutputs = 2;
        }
        else if (this.modelNumber === '-LQxjUuabdG7N0VUheIE') {
            // DTP844k
            numOutputs = 4;
        }
        else if (this.modelNumber === '-LQxjW6cqOwgv7nHi4RB') {
            // DTP864k
            numOutputs = 6;
        }
        else if (this.modelNumber === '-LQxjX4z4fD22aBP5sge') {
            // DTP1084k
            numInputs = 10;
            numOutputs = 8;
        }
        return [
            // TODO: Craig's comments say mute isn't supported for these channels.
            // line inputs (block A in Figure 54); note that these are the trim blocks, which are
            // appropriate for volume control; we do not include the gain blocks, which should be
            // used for equipment calibration
            ...createAudioChannels(MatrixChannel, range1d('A', 1, numInputs), (key, index) => [
                (30100 + 2 * index).toString(),
                (30100 + 2 * index + 1).toString(),
            ], { trim: { min: -120, max: 120 } }, // -12 dB to 12 dB
            'Input '),
            // microphone inputs (block B in Figure 54)
            ...createAudioChannels(MatrixChannel, range1d('B', 1, 4), 40100, {
                volume: { min: -380, max: 120 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, 'Mic '),
            // post matrix gain blocks (block F in Figure 54)
            ...createAudioChannels(MatrixChannel, range1d('F', 1, numOutputs), (key, index) => [
                (50000 + 2 * index).toString(),
                (50000 + 2 * index + 1).toString(),
            ], {
                volume: { min: -380, max: 120 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, 'Post Matrix Output '),
            // virtual returns (block D in Figure 54)
            ...createAudioChannels(MatrixChannel, range1d('D', 'A', 'H'), 50100, {
                volume: { min: -380, max: 120 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, 'Virtual Return '),
            // expansion inputs (block E in Figure 54)
            ...createAudioChannels(MatrixChannel, range1d('E', 1, 16), 50200, {
                volume: { min: -250, max: 250 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, (key, index) => `Expansion Input ${index + 1}`),
            // matrix outputs to outputs (block G in figure 54)
            ...createAudioChannels(MatrixChannel, Array(numOutputs)
                .fill(0)
                .map((el, index) => `G${index + 1}${index + 1}`), (key, index) => [
                (20000 + 2 * index * 101).toString(),
                (20000 + (2 * index + 1) * 101).toString(),
            ], {
                volume: { min: -250, max: 250 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, (key, index) => `Matrix Output ${key[1]} to Output ${key[2]}`),
            // mic in to audio outputs (block H in Figure 54)
            ...createAudioChannels(MatrixChannel, range2d('H', 1, 1, 4, numOutputs), (key, index) => {
                const base = 21600 + 100 * Math.trunc(index / numOutputs) + 2 * (index % numOutputs);
                return [base.toString(), (base + 1).toString()];
            }, {
                volume: { min: -250, max: 250 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, (key, index) => `Mic ${key[1]} to Output ${key.substring(2)}`),
            // virtual ret to audio outputs (block I in figure 54)
            ...createAudioChannels(MatrixChannel, range2d('I', 'A', 1, 'H', numOutputs), (key, index) => {
                const base = 22000 + 100 * Math.trunc(index / numOutputs) + 2 * (index % numOutputs);
                return [base.toString(), (base + 1).toString()];
            }, {
                volume: { min: -250, max: 250 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, (key, index) => `Virtual Return ${key[1]} to Output ${key.substring(2)}`),
            // expansion to audio outputs (block J in figure 54)
            ...createAudioChannels(MatrixChannel, range2d('J', 1, 1, 16, numOutputs), (key, index) => {
                const base = 22800 + 100 * Math.trunc(index / numOutputs) + 2 * (index % numOutputs);
                return [base.toString(), (base + 1).toString()];
            }, {
                volume: { min: -250, max: 250 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, (key, index) => {
                // supports multi-digit inputs and outputs, for example: J161, where the input
                // is 16 and the output is 1
                const inputNum = Math.trunc(index / numOutputs) + 1;
                const outputNum = (index % numOutputs) + 1;
                return `Expansion Input ${inputNum} to Output ${outputNum}`;
            }),
            // analog line outputs, HDMI/DTP outputs, and amplifier output (block K in figure 54)
            ...createAudioChannels(MatrixChannel, [
                ...range1d('KA', 1, numOutputs),
                ...range1d('KV', 1, numOutputs),
                'KP1',
                ...range1d('KD', (numOutputs >> 1) + 1, numOutputs),
            ], (key, index) => {
                var _a;
                const n = 2 * (parseInt(key[key.length - 1], 10) - 1);
                const base = ((_a = { A: 60000, V: 60200, P: 60016, D: 60000 }[key[1]]) !== null && _a !== void 0 ? _a : 60016) + n;
                return [base.toString(), (base + 1).toString()];
            }, {
                volume: { min: -500, max: 0 },
                trim: { min: -240, max: 0 },
            }, (key, index) => {
                var _a;
                const type = (_a = { A: 'Analog', V: 'Digital', D: 'Analog DTP' }[key[1]]) !== null && _a !== void 0 ? _a : 'Amp';
                return `Output ${key[key.length - 1]} ${type} Volume`;
            }),
            // groups (use "Z" so we don't conflict with figure labels above)
            ...Group.createAudioChannels(range1d('Z', 1, 32), false, undefined, {
                mixPoint: {
                    volume: { min: -250, max: 250 },
                    trim: { min: -120, max: 120 },
                },
                preMatrixTrim: {
                    volume: { min: -120, max: 120 },
                    trim: { min: -120, max: 120 },
                },
                preMixerGain: {
                    volume: { min: -380, max: 120 },
                    trim: { min: -120, max: 120 },
                },
                outputAttenuation: {
                    volume: { min: -500, max: 0 },
                    trim: { min: -240, max: 0 },
                },
            }),
        ];
    }
}
class ExtronDxpSeries {
    constructor(equipmentType, modelNumber, extronModel) {
        this.equipmentType = equipmentType;
        this.modelNumber = modelNumber;
        this.hasVerboseMode = true;
        this.queryVideoCommand = '%';
        this.useOutputInRouteCommands = true;
    }
    static deviceMatches(equipmentType, modelNumber, extronModel) {
        return (equipmentType === '-LCEDOmeRpYN8cVsR6cq' &&
            (modelNumber === '-LbU9Sdm1xjy-kaEEZwC' || // DXP 44 HD 4K
                modelNumber === '-LbU9WCsvA740ie3wMoz' || // DXP 84 HD 4K
                modelNumber === '-LbU9YwT1pBjaedvuHor' || // DXP 88 HD 4K
                modelNumber === '-LbU9abf1Np08r_AeFSJ' || // DXP 168 HD 4K
                modelNumber === '-LbU9gkPXxoLZ1nzS9I9') // DXP 1616 HD 4K
        );
    }
}
class ExtronIn1508AndIn1608 {
    constructor(equipmentType, modelNumber, extronModel) {
        // The In1608 has multiple outputs, but the same route goes to each of them. It doesn't
        // support specifying the output when setting or querying the route.
        this.equipmentType = equipmentType;
        this.modelNumber = modelNumber;
        this.hasVerboseMode = true;
        this.queryVideoCommand = '!';
        this.useOutputInRouteCommands = this.modelNumber !== '-LCEDWOgikcF8USId4zr';
    }
    static deviceMatches(equipmentType, modelNumber, extronModel) {
        return (equipmentType === '-LCEDOmeRpYN8cVsR6cq' &&
            (modelNumber === '-LCEDTpRifVwRzGvfXiJ' || // IN1508
                modelNumber === '-LCEDWOgikcF8USId4zr') // IN1608 xi
        );
    }
    getAudioChannels() {
        return [
            new VariableOutput('O1', {
                volume: { min: -50, max: 0 },
                trim: { min: -24, max: 0 },
            }, 'Variable Output'),
        ];
    }
}
class ExtronMvc121 {
    constructor(equipmentType, modelNumber, extronModel) {
        this.equipmentType = equipmentType;
        this.modelNumber = modelNumber;
        this.hasVerboseMode = true;
    }
    static deviceMatches(equipmentType, modelNumber, extronModel) {
        return (equipmentType === '-LvpXhNbQRIOWO_3gtUS' &&
            (modelNumber === '-Mj-EVZvCCXtRCgfJ-QO' ||
                ((modelNumber === '-Mj-Ei1OBYjLMZLZE-D9' || modelNumber === undefined) &&
                    extronModel === 'mvc121plus')));
    }
    getAudioChannels() {
        return [
            ...createAudioChannels(CommonMute, range1d('I', 1, 3), (key, index) => { var _a; return (_a = ['40000', '40001', ['30000', '30001']][index]) !== null && _a !== void 0 ? _a : ''; }, undefined, (key, index) => `${index < 2 ? 'Mic-Line' : 'Line'} In ${index + 1}`, (key, index) => `\x1b${index + 1}NI`),
            ...createAudioChannels(Crosspoint, range1d('X', 1, 3), (key, index) => [
                (20000 + 100 * index).toString(),
                (20000 + 100 * index + 1).toString(),
            ], {
                volume: { min: -24, max: 12 },
                trim: { min: -12, max: 12 }, // -12 dB to 12 dB
            }, (key, index) => `Crosspoint I${key[1]} to O1`),
            new VariableOutput('O1', {
                // The volume output range is 0 to 100%. I took measurements and found that a 1% change
                // corresponds to 0.90 - 0.96 dB. However, a volume setting of 52-51 causes very bad
                // distortion, and the signal is lost for volume settings <= 50. So, we limit the volume
                // range to 50-100 even though it only corresponds to a range of ~43 dB.
                volume: { min: 50, max: 100 },
                trim: { min: 76, max: 100 }, // ~24 dB range
            }, 'Variable Output', '\x1b1NO'),
            new CommonMute('O2', ['60002', '60003'], undefined, 'Fixed Output', '\x1b2NO'),
        ];
    }
}
class ExtronNetPaSeries {
    constructor(equipmentType, modelNumber, extronModel) {
        this.equipmentType = equipmentType;
        this.modelNumber = modelNumber;
        this.hasVerboseMode = true;
    }
    static deviceMatches(equipmentType, modelNumber, extronModel) {
        return (equipmentType === '-LvpXhNbQRIOWO_3gtUS' &&
            (modelNumber === '-NtLdBa9uJZwZPXgotcz' || modelNumber === '-NtLdCs7hPY8jWIhogMe'));
    }
    getAudioChannels() {
        const modelName = this.modelNumber === '-NtLdCs7hPY8jWIhogMe' ? 'NetPA 1004' : 'NetPA 1002';
        const numMicInputs = 4;
        const numAmpOutputs = modelName === 'NetPA 1002' ? 2 : 4;
        const numAtChans = modelName === 'NetPA 1002' ? 2 : 4; // Dante input and output channels
        const numLineOutputs = modelName === 'NetPA 1002' ? 2 : 4;
        const numOutChans = numLineOutputs + numAmpOutputs + numAtChans;
        const numInChans = numMicInputs + numAtChans;
        return [
            ...createAudioChannels(MatrixChannel, range1d('I', 1, numMicInputs), 40100, {
                volume: { min: -380, max: 120 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, 'Mic Input '),
            ...createAudioChannels(MatrixChannel, range1d('D', 1, numAtChans), 50100, {
                volume: { min: -380, max: 120 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, 'AT Input '),
            ...createAudioChannels(MatrixChannel, range2d('X', 1, 1, numInChans, numOutChans), (key, index) => (20000 +
                100 * Math.floor(index / numOutChans) +
                (index % numOutChans)).toString(), {
                volume: { min: -240, max: 120 },
                trim: { min: -120, max: 120 }, // -12 dB to 12 dB
            }, (key, index) => `Crosspoint ${parseInt(key[1]) <= numMicInputs
                ? 'Mic Input ' + key[1]
                : parseInt(key[1]) <= numMicInputs + numAtChans
                    ? 'AT Input ' + (parseInt(key[1]) - numMicInputs)
                    : key[1]} to ${parseInt(key.substring(2)) <= numAmpOutputs
                ? 'Amp Output ' + key.substring(2)
                : parseInt(key.substring(2)) <= numAmpOutputs + numLineOutputs
                    ? 'Line Output ' + (parseInt(key[2]) - numAmpOutputs)
                    : parseInt(key.substring(2)) <=
                        numAmpOutputs + numLineOutputs + numAtChans
                        ? 'AT Output ' +
                            (parseInt(key.substring(2)) - numAmpOutputs - numLineOutputs)
                        : 'Output' + key.substring(2)}`),
            ...createAudioChannels(MatrixChannel, range1d('A', 1, numAmpOutputs), 60000, {
                volume: { min: -500, max: 0 },
                trim: { min: -240, max: 0 }, // -24 dB to 0 dB
            }, 'Amp Output '),
            ...createAudioChannels(MatrixChannel, range1d('L', 1, numLineOutputs), 60002, {
                volume: { min: -500, max: 0 },
                trim: { min: -240, max: 0 }, // -24 dB to 0 dB
            }, 'Line Output '),
            ...createAudioChannels(MatrixChannel, range1d('T', 1, numAtChans), 60004, {
                volume: { min: -500, max: 0 },
                trim: { min: -240, max: 0 }, // -24 dB to 0 dB
            }, 'AT Output '),
            // Groups do not use zero pad on channel numbers returned
            ...Group.createAudioChannels(range1d('G', 1, 32), false, undefined, {
                mixPoint: {
                    volume: { min: -250, max: 250 },
                    trim: { min: -120, max: 120 },
                },
                preMatrixTrim: {
                    volume: { min: -120, max: 120 },
                    trim: { min: -120, max: 120 },
                },
                preMixerGain: {
                    volume: { min: -380, max: 120 },
                    trim: { min: -120, max: 120 },
                },
                outputAttenuation: {
                    volume: { min: -500, max: 0 },
                    trim: { min: -240, max: 0 },
                },
            }),
        ];
    }
}
class ExtronOther {
    constructor(equipmentType, modelNumber, extronModel) {
        this.equipmentType = equipmentType;
        this.modelNumber = modelNumber;
        this.hasVerboseMode = true;
        this.queryVideoCommand = '!';
        this.useOutputInRouteCommands = true;
    }
    static deviceMatches(equipmentType, modelNumber, extronModel) {
        return (equipmentType === '-LCEDOmeRpYN8cVsR6cq' &&
            (modelNumber === '-LL5Wcz2PL427UYTBnS3' || // SW2 HD 4K
                modelNumber === '-LL5WebTl0L6P9B5iC-p' || // SW4 HD 4K
                modelNumber === '-LL5WgAzpQ05jZCnCZJz' || // SW6 HD 4K
                modelNumber === '-LL5WibjXH0m-VDcEJ1r' || // SW8 HD 4K
                modelNumber === '-LWXbVuCtpk17uYbSkKZ' || // SW2 USB
                modelNumber === '-LWXbmcF2V3qKblJSL4b' || // SW4 USB
                modelNumber === '-LWXbq323tIAUSw0g9z0' || // SW4 USB Plus
                modelNumber === '-M240A3k-uQCw69yi4cJ' || // IN1806
                modelNumber === '-M240Cm32g-Sgc9SZyrF' || // IN1808
                modelNumber === '-MCx2b-tTmZrOO_JKhxu') // IN1604
        );
    }
}
class ExtronVc10 {
    constructor(equipmentType, modelNumber, extronModel) {
        this.equipmentType = equipmentType;
        this.modelNumber = modelNumber;
        this.hasVerboseMode = false;
    }
    static deviceMatches(equipmentType, modelNumber, extronModel) {
        return (equipmentType === '-LvpXhNbQRIOWO_3gtUS' &&
            (modelNumber === '-Mj-EbE5cfM_aejN4AgO' ||
                ((modelNumber === '-Mj-Ei1OBYjLMZLZE-D9' || modelNumber === undefined) &&
                    extronModel === 'mlavc10plus')));
    }
    getAudioChannels() {
        return [
            new VariableOutput('O1', {
                // The VC10 can be in logarithmic or linear taper mode. Some amps perform the
                // linear to logarithmic conversion automatically. The default setting for the
                // VC10 is linear mode, which is the proper setting for all Extron amplifiers.
                // The following ranges were determined by using the VC10 with MPA401 and
                // measuring the voltage outputs.
                volume: { min: 40, max: 100 },
                trim: { min: 71, max: 100 }, // ~24 dB range
            }, 'Variable Output'),
        ];
    }
}
function getFilters(channel) {
    return {
        amplifier: channel.hasVolume,
        assistiveListeningMute: channel.hasMute,
        assistiveListeningMuteStatus: false,
        assistiveListeningVolume: channel.hasVolume,
        audioInputMute: channel.hasMute,
        audioInputMuteStatus: false,
        audioInputVolume: channel.hasVolume,
        mics: channel.hasMute,
        micsMuteStatus: false,
        overflowRoomMute: channel.hasMute,
        overflowRoomMuteStatus: false,
        overflowRoomVolume: channel.hasVolume,
        soundReinforcementMute: channel.hasMute,
        soundReinforcementMuteStatus: false,
        soundReinforcementVolume: channel.hasVolume,
        telephony: false,
        receiveGain: false,
        preset: false,
    };
}
export class Extron {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.audioChannels = [];
        this.muteConfigs = {};
        this.volumeConfigs = {};
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: 'N',
                unterminatedRegExp: /Password:/i,
                succeed: /^(?:Pno)*\d{2,}-\d{3,}-\d{2}[0-9a-z]*/i,
                timeoutMs: 8000,
                next: /Password:/i,
            },
            {
                errorCode: 'authFailed',
                send: (credentials) => {
                    return `${credentials.password}`;
                },
                // Successful login returns Login Administrator or Login User.
                unterminatedRegExp: /Login|Password:/i,
                timeoutMs: 8000,
                next: /Login/i,
            },
        ];
        this.pollIndex = 0;
        this.pollFunctions = [];
        this.numVideoOutputs = 0;
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return Promise.all(this.audioChannels.map((channel) => __awaiter(this, void 0, void 0, function* () {
                    yield channel.queryName(this.socket, [`get.detected.name.${channel.key}`]);
                    yield channel.queryCaps(this.socket, [`get.detected.caps.${channel.key}`]);
                    return Object.assign(Object.assign(Object.assign({ channel: channel.key }, (channel.gainConfig
                        ? { gainConfig: channel.gainConfig }
                        : undefined)), (channel.hasStereoConfig ? { hasStereoConfig: true } : undefined)), { name: channel.name, filters: getFilters(channel) });
                })));
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send('*Q', /(?:Bld)?(.+)/, tags);
                return match[1];
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const config = this.muteConfigs[key];
                const channel = this.getAudioChannel(key);
                return (_a = (yield (channel === null || channel === void 0 ? void 0 : channel.queryMute(config, this.socket, tags)))) !== null && _a !== void 0 ? _a : false;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b;
                const config = this.muteConfigs[key];
                const channel = this.getAudioChannel(key);
                return (_b = (yield (channel === null || channel === void 0 ? void 0 : channel.setMute(config, value, this.socket, tags)))) !== null && _b !== void 0 ? _b : false;
            }),
            getKey: (config) => this.getKey(config),
            validate: (config, info) => __awaiter(this, void 0, void 0, function* () {
                const key = this.getKey(config);
                const channel = this.getAudioChannel(key);
                if (channel === undefined)
                    return 'missing';
                if (!getFilters(channel)[info.filter])
                    return 'incompatible';
                channel.isMuteConfigured = true;
                this.pollFunctions.push(() => __awaiter(this, void 0, void 0, function* () {
                    const tags = [`get.mute.${channel.key}`, `poll`];
                    const value = yield channel.queryMute(config, this.socket, tags);
                    this.state.mutes.update(key, value);
                }));
                this.muteConfigs[key] = config;
                return 'ok';
            }),
            asyncHandlers: {
                match: (data) => {
                    const values = [];
                    for (const [key, config] of Object.entries(this.muteConfigs)) {
                        const channel = this.getAudioChannel(key);
                        if (channel === undefined)
                            continue;
                        const value = channel.processMute(config, data);
                        if (value !== undefined)
                            values.push([key, value]);
                    }
                    return values.length > 0 ? values : undefined;
                },
                callback: (value) => {
                    for (const entry of value) {
                        this.state.mutes.update(entry[0], entry[1]);
                    }
                },
            },
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                if (this.device === undefined)
                    throw new Error(`device undefined`);
                const match = yield this.socket.send(this.numVideoOutputs > 1 && this.device.useOutputInRouteCommands
                    ? `${key}${this.device.queryVideoCommand}`
                    : `${this.device.queryVideoCommand}`, /Out(\d+)\sIn(\d+)\s(?:All|Vid)|^(?:Chn|In)?(\d+)|^E\d+/i, tags);
                if (match[3])
                    return match[3];
                if (match[2])
                    return match[2];
                return this.state.videoMatrixRoutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (this.device === undefined)
                    throw new Error(`device undefined`);
                const match = yield this.socket.send(this.numVideoOutputs > 1 && this.device.useOutputInRouteCommands
                    ? `${value}*${key}!`
                    : `${value}!`, /Out(\d+)\sIn(\d+)\s(?:All|Vid)|^(?:Chn|In)?(\d+)|^E\d+/i, tags);
                if (match[3])
                    return match[3];
                if (match[2])
                    return match[2];
                return this.state.videoMatrixRoutes.values[key];
            }),
            asyncHandlers: {
                regExp: /Out(\d+)\sIn(\d+)\s(?:All|Vid)|^(?:Chn|In)?(\d+)/i,
                callback: (match) => {
                    const key = match[1] ? match[1] : '1';
                    const value = match[2] ? match[2] : match[3];
                    this.state.videoMatrixRoutes.update(key, value);
                },
            },
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () {
                var _c;
                return Object.keys((_c = config === null || config === void 0 ? void 0 : config.videoOutputs) !== null && _c !== void 0 ? _c : {}).length > 0;
            }),
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _d;
                const config = this.volumeConfigs[key];
                const channel = this.getAudioChannel(key);
                return (_d = (yield (channel === null || channel === void 0 ? void 0 : channel.queryVolume(config, this.socket, tags)))) !== null && _d !== void 0 ? _d : 0;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _e;
                const config = this.volumeConfigs[key];
                const channel = this.getAudioChannel(key);
                return (_e = (yield (channel === null || channel === void 0 ? void 0 : channel.setVolume(config, value, this.socket, tags)))) !== null && _e !== void 0 ? _e : 0;
            }),
            getKey: (config) => this.getKey(config),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                var _f;
                const key = this.getKey(config);
                const channel = this.getAudioChannel(key);
                return (_f = channel === null || channel === void 0 ? void 0 : channel.getVolumeRange(config, info.volumeType)) !== null && _f !== void 0 ? _f : { max: 0, min: 0, round: 1 };
            }),
            validate: (config, info) => __awaiter(this, void 0, void 0, function* () {
                const key = this.getKey(config);
                const channel = this.getAudioChannel(key);
                if (channel === undefined)
                    return 'missing';
                if (!getFilters(channel)[info.filter])
                    return 'incompatible';
                if (channel.getVolumeRange(config, info.volumeType) === undefined) {
                    return 'incompatible';
                }
                channel.isVolumeConfigured = true;
                this.pollFunctions.push(() => __awaiter(this, void 0, void 0, function* () {
                    const tags = [`get.volume.${channel.key}`, `poll`];
                    const value = yield channel.queryVolume(config, this.socket, tags);
                    this.state.volumes.update(key, value);
                }));
                this.volumeConfigs[key] = config;
                return 'ok';
            }),
            asyncHandlers: {
                match: (data) => {
                    const values = [];
                    for (const [key, config] of Object.entries(this.volumeConfigs)) {
                        const channel = this.getAudioChannel(key);
                        if (channel === undefined)
                            return;
                        const value = channel.processVolume(config, data);
                        if (value !== undefined)
                            values.push([key, value]);
                    }
                    return values.length > 0 ? values : undefined;
                },
                callback: (value) => {
                    for (const entry of value) {
                        this.state.volumes.update(entry[0], entry[1]);
                    }
                },
            },
        };
        // polling
        this.polls = [
            {
                function: () => __awaiter(this, void 0, void 0, function* () {
                    var _g, _h;
                    if (this.pollFunctions.length > 0) {
                        yield ((_h = (_g = this.pollFunctions)[this.pollIndex]) === null || _h === void 0 ? void 0 : _h.call(_g));
                        this.pollIndex++;
                        if (this.pollIndex >= this.pollFunctions.length)
                            this.pollIndex = 0;
                    }
                    else {
                        const value = yield this.firmwareVersion.get(['get.firmwareVersion', 'poll']);
                        this.state.firmwareVersion.update(value);
                    }
                }),
                intervalMs: 2000,
            },
            { intervalMs: 3000, controls: ['videoMatrixRoutes'] },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            this.pollFunctions.length = 0;
            this.pollIndex = 0;
            this.muteConfigs = {};
            this.volumeConfigs = {};
            this.numVideoOutputs = Object.keys((_a = config === null || config === void 0 ? void 0 : config.videoOutputs) !== null && _a !== void 0 ? _a : {}).length;
            if (config.equipmentType !== ((_b = this.device) === null || _b === void 0 ? void 0 : _b.equipmentType) ||
                config.modelNumber !== ((_c = this.device) === null || _c === void 0 ? void 0 : _c.modelNumber)) {
                this.device = yield this.getDevice(config.equipmentType, config.modelNumber);
                if (this.device === undefined)
                    throw new Error(`unsupported device`);
            }
            this.audioChannels = (_f = (_e = (_d = this.device).getAudioChannels) === null || _e === void 0 ? void 0 : _e.call(_d)) !== null && _f !== void 0 ? _f : [];
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () { });
    }
    onInitialize(credentials, config) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
            if (result !== 'connected')
                return result;
            this.device = yield this.getDevice(config.equipmentType, config.modelNumber);
            if (this.device === undefined)
                return 'verifyFailed';
            return result;
        });
    }
    // other class methods
    getAudioChannel(key) {
        key = key.split('-')[0];
        return this.audioChannels.find(channel => channel.key === key);
    }
    getDevice(equipmentType, modelNumber) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let extronModel;
            try {
                const match = yield this.socket.send(`1I`, /^(?:Inf01\*)?(.+)|^E\d+/, [
                    'get.model',
                    'initialize',
                ]);
                extronModel = (_a = match[1]) === null || _a === void 0 ? void 0 : _a.toLowerCase().replace(/\s+/g, '');
            }
            catch (error) {
                extronModel = undefined;
            }
            let deviceConstructor = Extron.devices.find(dev => dev.deviceMatches(equipmentType, modelNumber, extronModel));
            if (deviceConstructor === undefined) {
                const equipmentInfo = `equipmentType = ${equipmentType}`;
                const modelInfo = `modelNumber = ${modelNumber}`;
                const extronInfo = `extronModel = ${extronModel}`;
                this.log.error(`unrecognized device: ${equipmentInfo}, ${modelInfo}, ${extronInfo}`);
                return undefined;
            }
            const device = new deviceConstructor(equipmentType, modelNumber, extronModel);
            if (device.hasVerboseMode)
                yield this.setVerboseMode();
            return device;
        });
    }
    getKey(config) {
        if (typeof config === 'string')
            return config;
        return (config.channel +
            (config.stereo !== undefined && config.stereo !== 'stereo' ? `-${config.stereo}` : ''));
    }
    setVerboseMode() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.socket.send('\x1b2CV', /(?:Vrb)?(\d)|^E\d+/i, ['set.verbose', 'initialize']);
        });
    }
}
// static properties
Extron.channelsConfigName = 'extronVolumeControlControlChannels';
Extron.driverType = 'socketDriver';
Extron.key = '-LCEDOmeRpYN8cVsR6cq';
Extron.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\r', '\n'],
    },
    sendTerminator: '\r',
    sendTimeoutMs: 3000,
};
Extron.socketOptions = [
    Object.assign(Object.assign({}, Extron.baseSocketOptions), { port: 4999 }),
    Object.assign(Object.assign({}, Extron.baseSocketOptions), { port: 23 }),
];
Extron.devices = [
    ExtronDmp44,
    ExtronDmp64,
    ExtronDtp4kSeries,
    ExtronDtpSeries,
    ExtronDxpSeries,
    ExtronIn1508AndIn1608,
    ExtronMvc121,
    ExtronNetPaSeries,
    ExtronOther,
    ExtronVc10,
];
